import type { OpenPanel } from '@openpanel/web';

// OpenPanel web analytics, self-hosted on our own Azure box. Marketing-side
// attribution: where visitors come from and which pages they land on, alongside
// PostHog's product analytics.
//
// The host is a getanyapi.com subdomain served through a Cloudflare Tunnel that
// forwards only `/api/track`, and its label carries no analytics wording, because
// a filter list matches on the hostname: a name like `analytics.` is blocked.
//
// Client-only and lazily imported. The SDK reads document/window and installs
// history listeners in its constructor, so it must never run during SSR; the
// dynamic import also keeps it out of the initial bundle, matching how PostHog is
// deferred off the critical path in `Providers.tsx`.
//
// The client id and api url are public build-time values
// (deploy/frontend.env.production). When either is unset - local dev, preview
// builds - the SDK is never fetched and nothing is sent.
const clientId: string | undefined = import.meta.env.VITE_OPENPANEL_CLIENT_ID;
const apiUrl: string | undefined = import.meta.env.VITE_OPENPANEL_API_URL;

// Module-level, not component state: React's StrictMode runs effects twice in
// dev and `Providers` can remount, and two constructors would install two
// history listeners and double-count every pageview.
let started: Promise<OpenPanel | null> | null = null;

// Set before the client exists, by a surface that knows an identity is coming.
//
// The SDK sends its first screen view on a `setTimeout(0)` from its own
// constructor, and `identify` only attaches a profile to events sent AFTER it
// resolves. On an authenticated surface those two race, and the constructor
// always wins: measured on 2026-09-13, every event from both accounts that
// signed up that day carried the anonymous device hash and not one carried the
// Clerk id, so the accounts existed in OpenPanel with no activity against them.
//
// Nothing in the server backfills this. Its identify handler only upserts the
// profile row, and its dashboard joins a person's anonymous history to their
// account through events that carry the profile id - so a device that never
// sends one stays anonymous forever, including the landing page and referrer
// that brought them.
//
// So when an identity is coming, the client starts queued and is released once
// Clerk has answered, which makes that first screen view carry the profile.
let identityPending = false;

/**
 * Initialize OpenPanel once per document, resolving to the client (or `null`
 * when analytics is off). Safe to call repeatedly and safe to call during SSR.
 */
export function startOpenPanel(): Promise<OpenPanel | null> {
  if (started) return started;
  if (typeof window === 'undefined' || !clientId || !apiUrl) return Promise.resolve(null);

  started = import('@openpanel/web')
    .then(
      ({ OpenPanel: Client }) =>
        new Client({
          clientId,
          apiUrl,
          // Queue rather than drop: `ready` flushes everything held, with the
          // profile attached. Read at construction, so a client that is
          // already running is never retroactively gated.
          disabled: identityPending,
          // TanStack Router navigates through the history API, and the SDK's
          // screen-view tracking hooks it, so this covers the initial load and
          // every client-side route change without a router subscription of
          // our own.
          trackScreenViews: true,
        }),
    )
    .catch(
      // Analytics never breaks the app shell. A failed load (offline, blocked
      // by an extension) just means no events; retrying would repeat it.
      () => null,
    );
  return started;
}

/**
 * Hold events until `releaseOpenPanel` runs, because the caller knows whether
 * this visitor is signed in and OpenPanel does not.
 *
 * Only affects a client that has not started yet. A client-side navigation from
 * a marketing page into the dashboard has already sent anonymous screen views,
 * and those stitch to the account through the identified events that follow.
 */
export function holdOpenPanelForIdentity(): void {
  identityPending = true;
}

/**
 * Let held events go, after the identity is known (or known to be absent).
 *
 * Must run on every path out of `holdOpenPanelForIdentity`, including a signed
 * out visitor, or that page's views are never sent at all.
 */
export async function releaseOpenPanel(): Promise<void> {
  identityPending = false;
  if (!started) return;
  const client = await started;
  client?.ready();
}

/**
 * Attach the signed-in account to the device OpenPanel is already following.
 *
 * This is also what makes a purchase attributable. The gateway reports a
 * settled top-up against the Clerk user id, and OpenPanel attaches that
 * server-sent event to whichever live session carries the same profile - so a
 * visit that never identifies here can be paid for and still land as revenue
 * from nobody. Measured on our own instance on 2026-09-13.
 */
export async function identifyOpenPanelUser(
  userId: string,
  properties: { email?: string; name?: string },
): Promise<void> {
  const client = await startOpenPanel();
  if (!client) return;
  try {
    await client.identify({
      profileId: userId,
      email: properties.email,
      firstName: properties.name,
    });
  } catch {
    // Identity is a nice-to-have; the pageview stream stands without it.
  }
}

/**
 * Drop the current identity on sign-out, so the next person on a shared device
 * is not followed as the previous one.
 *
 * Deliberately does not initialize OpenPanel: `clear` discards the device id, so
 * running it for a visitor who was never signed in would throw away the very
 * attribution we are here to collect.
 */
export async function resetOpenPanel(): Promise<void> {
  if (!started) return;
  const client = await started;
  if (!client) return;
  try {
    client.clear();
  } catch {
    // Nothing the app can do, and nothing the customer should see.
  }
}
